import React, {ReactElement, useCallback, useLayoutEffect, useMemo, useState} from 'react';
import './TabBar.less';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {isPortalUser} from "@/lib/picos/currentLogin";
import type {IconProp} from "@fortawesome/fontawesome-svg-core";
import useComponentCache from "@/hooks/useComponentCache";

type TabBarState = {
    selectedTab: string,
}

function TabBar(props: TabBarProps) {
    const {tabs, iconsOnly, onClick, name, selectedIndex} = props;

    // Map to the component cache in redux
    const {cacheSave, cacheValue} = useComponentCache<TabBarState>(name);

    // Use the first tab as the selected tab to start
    const [selectedTab, setSelectedTab] = useState(cacheValue('selectedTab', tabs[selectedIndex]?.title));

    // Save state
    useLayoutEffect(() => {
        return () => cacheSave({selectedTab});
    }, [cacheSave, selectedTab]);

    // Handler for tab clicks
    const onTabClick = useCallback((title: string, displayTitle: string | ReactElement) => {
        setSelectedTab(title);
        onClick(title, displayTitle);
    }, [onClick, setSelectedTab]);

    // Generate the tabs
    const titleBoxes = useMemo(() => tabs.map((child, index) => {
        // We use the tab's title to determine if it is selected by matching with the selected state
        const _selectedIndex = tabs.findIndex(tab => tab.title === selectedTab);

        const {title, displayTitle, customStyles, icon} = child;

        // If there is only one tab, don't bother selecting it
        const isSelected = tabs.length > 1 && _selectedIndex === index;

        // Use a class to select the top of the tab, if selected
        const selClass = isSelected ? 'tab-bar__title-box-selected' : '';

        // Give the tab a name
        const titleBoxName = <div className="tab-bar__title-box-name">{displayTitle}</div>;

        return <div key={`${name}-${title}`}
                    className={`tab-bar__title-box ${selClass} ${customStyles}`}
                    onClick={() => onTabClick(title, displayTitle)}
        >
            <div className="tab-bar__icon">
                <FontAwesomeIcon icon={icon}/></div>
            {iconsOnly ? null : titleBoxName}
        </div>
    }), [tabs, selectedTab, name, onTabClick, iconsOnly]);

    // Return the tab bar
	const className = `tab-bar__title-bar${isPortalUser() ? ' tab-bar--portal' : ''}`
    return <div className={className}>{titleBoxes}</div>;
}

export type Tab = {
    title: string,
    displayTitle: string | ReactElement,
    customStyles: string,
    icon: IconProp
};

export type TabBarProps = {
    // Unique name for this tab bar
    name: string,

    // Tabs to display
    tabs: Tab[],

    // Only show icons
    iconsOnly?: boolean,

    // Called when a tab changes
    onClick: (title: string, displayTitle: string | ReactElement) => void,

    // Index of the selected index (if any)
    selectedIndex?: number,
};

export default TabBar;
